import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeyboardNav } from '@/utils/keyboardNav';
import { useAuthStore } from '@/stores/authStore';
import { useAuth } from '@/contexts/AuthContext';

function getHomePath(role: string) {
  return role === 'SECURITY_GUARD' ? '/guard/dashboard' : '/';
}

export default function LoginPage() {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  useKeyboardNav(formRef as React.RefObject<HTMLElement>);

  const { login: contextLogin } = useAuth();
  const loginStore = useAuthStore((s) => s.login);
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const storeError = useAuthStore((s) => s.error);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [localError, setLocalError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const displayError = localError || storeError;

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.must_change_password) {
        navigate('/change-password', { replace: true });
      } else {
        navigate(getHomePath(user.role), { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  const clearError = () => {
    setLocalError('');
    useAuthStore.getState().clearError();
  };

  const emailError = submitted && !email.trim() ? 'Email is required' : '';
  const passwordError = submitted && !password ? 'Password is required' : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    clearError();

    if (!email.trim() || !password) return;

    setIsLoading(true);
    try {
      const success = await loginStore(email.trim(), password);
      if (success) {
        const loggedUser = useAuthStore.getState().user;
        const token = useAuthStore.getState().accessToken;
        if (loggedUser) {
          contextLogin({
            id: loggedUser.id,
            email: loggedUser.email,
            name: loggedUser.name,
            role: loggedUser.role,
            companyId: loggedUser.companyId,
            token: token || undefined,
            permissions: (loggedUser.permissions as any) || {},
            additionalControls: loggedUser.additionalControls || {},
            must_change_password: loggedUser.must_change_password,
          } as any);

          if (loggedUser.must_change_password) {
            navigate('/change-password', { replace: true });
          } else {
            navigate(getHomePath(loggedUser.role), { replace: true });
          }
        }
      } else {
        const msg = useAuthStore.getState().error || 'Invalid email or password';
        setLocalError(msg);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setLocalError(err.message || 'Unable to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#1e293b] to-slate-800 flex items-center justify-center p-4">
      {/* Background glow effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#4f46e5]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#4f46e5]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[#4f46e5] flex items-center justify-center mb-4 shadow-lg shadow-indigo-500/30">
              <i className="ri-building-2-line text-white text-3xl" />
            </div>
            <h1 className="text-2xl font-bold text-[#1e293b] tracking-tight">InvenPro</h1>
            <p className="text-sm text-[#64748b] mt-1">Sign in to your workspace</p>
          </div>

          {/* Error banner */}
          {displayError && (
            <div
              role="alert"
              className="mb-4 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200"
            >
              <i className="ri-error-warning-fill text-red-500 text-sm shrink-0" aria-hidden="true" />
              <p className="text-sm text-red-600 flex-1">{displayError}</p>
              <button
                type="button"
                onClick={clearError}
                aria-label="Dismiss error"
                className="w-4 h-4 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>
          )}

          {/* Form */}
          <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-4">
            {/* Email */}
            <div>
              <label
                htmlFor="login-email"
                className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1.5"
              >
                Email Address <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm pointer-events-none" aria-hidden="true" />
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (displayError) clearError();
                  }}
                  placeholder="name@company.com"
                  autoComplete="email"
                  autoFocus
                  data-nav-index="0"
                  className={`w-full h-10 pl-9 pr-3 rounded-lg border text-sm text-[#1e293b] bg-white
                    focus:outline-none focus:ring-2 transition-colors ${
                      emailError || displayError
                        ? 'border-red-400 focus:ring-red-200'
                        : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                    }`}
                />
              </div>
              {emailError && (
                <p id="email-error" className="mt-1 text-xs text-red-500" role="alert">{emailError}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="login-password"
                className="block text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-1.5"
              >
                Password <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm pointer-events-none" aria-hidden="true" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (displayError) clearError();
                  }}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  data-nav-index="1"
                  className={`w-full h-10 pl-9 pr-10 rounded-lg border text-sm text-[#1e293b] bg-white
                    focus:outline-none focus:ring-2 transition-colors ${
                      passwordError || displayError
                        ? 'border-red-400 focus:ring-red-200'
                        : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                    }`}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#4f46e5] transition-colors cursor-pointer"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} aria-hidden="true" />
                </button>
              </div>
              {passwordError && (
                <p id="password-error" className="mt-1 text-xs text-red-500" role="alert">{passwordError}</p>
              )}
            </div>

            {/* Forgot password */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                data-nav-index="2"
                className="text-xs text-[#4f46e5] hover:text-indigo-700 font-medium cursor-pointer transition-colors"
              >
                Forgot password?
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              data-nav-index="3"
              className="w-full h-10 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold
                hover:bg-indigo-700 transition-colors
                disabled:opacity-60 disabled:cursor-not-allowed
                cursor-pointer flex items-center justify-center gap-2 mt-2 whitespace-nowrap"
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true" />
                  Signing in…
                </>
              ) : (
                <>
                  <i className="ri-login-box-line text-base" aria-hidden="true" />
                  Sign In
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-500 mt-5">
          InvenPro ERP &copy; 2026 — Keyboard-first. GST-ready.
        </p>
      </div>
    </div>
  );
}